import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterEvent, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, skipWhile, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { ArrayUtils } from '../../utils/array.utils';
import { environment } from '../../../environments/environment';
import { redirectToUrlExecuted } from '../../actions/app.actions';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { CatalogFacade } from '../../facades/catalog.facade';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { IAbstractProductIncluded, ICatalogProduct, IProductLabel, IValueFacets } from '../../models/abstract-product.models';
import {
  ICatalogSearchIncluded,
  ICategoryNode,
  IContractParams,
  ICurrentParams,
  IEquipmentComponentValues,
  IEquipmentValues,
  IPagination as ICatalogPagination,
  IPartsCategory,
  IProductIncluded,
  ISdmIbComponents,
  ISort,
} from '../../models/catalog.models';
import { IPagination, ISortOption } from '../../models/settings.model';
import { IEquipmentTypeFilter } from '../../models/equipment-filter.model';
import { CategoryUtils } from '../../utils/category.utils';
import { ObjectUtils } from '../../utils/object.utils';
import { ProductUtils } from '../../utils/product.utils';
import { StringUtils } from '../../utils/string.utils';
import { ISystemDetails } from '../../models/common.models';
import { ICart } from '../../models/cart.models';
import { EFeatureToggles, EStoreTypes, EUserRoles } from '../../configurations/common';
import { AppUtils } from '../../utils/app.utils';
import { InstallBaseFacade } from '../../facades/install-base.facade';
import { ISimpleInstallBaseProduct } from '../../models/installedbase.models';
import { CustomerFacade } from '../../facades/customer.facade';
import { SparePartsUtils } from '../../utils/spare-parts.utils';
import { httpContractParams, httpParams, notFilterableAttributesData, warningMessageData } from './catalog-data';
import { IEquipmentComponentFilter } from '../../models/equipment-component-filter.model';
import { Store } from '@ngrx/store';
import { selectIsLoginInProcess } from '../../auth/reducers/auth.reducers';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  standalone: false,
})
export class CatalogComponent implements OnInit, OnDestroy, AfterViewChecked {
  private router: Router = inject(Router);
  private store: Store<Parameters<typeof selectIsLoginInProcess>[0]> = inject<Store<Parameters<typeof selectIsLoginInProcess>[0]>>(Store);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private translate: TranslateService = inject(TranslateService);
  private catalogFacade: CatalogFacade = inject(CatalogFacade);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private marketingFacade: MarketingFacade = inject(MarketingFacade);
  private installBaseFacade: InstallBaseFacade = inject(InstallBaseFacade);
  private changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private translateService: TranslateService = inject(TranslateService);
  private customerFacade: CustomerFacade = inject(CustomerFacade);


  abstractProductIncluded!: IAbstractProductIncluded[];
  abstractProducts: ICatalogProduct[] = [];
  activatedRouteSubscription!: Subscription;
  activeEquipmentType: string[] = [];
  activeEquipmentComponent: string[] = [];
  activeRoute!: string;
  baseRouteUrl!: string;
  blackFridaySticker: string = environment.blackFridaySticker;
  blockedSortByOptions!: string[];
  catalogTitle: string = '';
  categoriesLoaded: boolean = false;
  categoryTree!: ICategoryNode[];
  contractParams: IContractParams = httpContractParams;
  currentCart!: ICart;
  currentCartId!: string;
  currentLayoutType: string = 'grid';
  fetchedCategories!: any;
  filters: any = [];
  filtersDropdownOpened: boolean = false;
  firstPageLoad: boolean = true;
  hasContractParams: boolean = false;
  installBaseProduct!: ISimpleInstallBaseProduct;
  isBusinessPartner: boolean = false;
  isCaStore: boolean = false;
  isCpqEnabled: boolean = false;
  isMyInstalledBaseFlowEnabled$: Observable<boolean> = new Observable<boolean>();
  isSparePartsEnabled: boolean = false;
  isUsStore: boolean = false;
  isMeaStore: boolean = false;
  labels: IProductLabel[] = [];
  loading: boolean = false;
  loadingCartDataInProgress: boolean = true;
  noProductFound: boolean = false;
  notFilterableAttributes: string[] = notFilterableAttributesData;
  pagination!: IPagination;
  params: ICurrentParams = httpParams;
  selectedFl!: string;
  sort!: ISortOption[];
  systemDetails!: ISystemDetails;
  warningWasShown: boolean = false;
  equipmentComponentFilterActive: boolean = false;
  isPredefinedEquipmentFlowEnabled$: Observable<boolean> = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW);
  storeContactEmail: string = AppUtils.getCurrentStore().address?.default?.emailAddress ?? '';
  isEnhancedEquipmentTypeFilterEnabled$: Observable<boolean> = this.configurationFacade.isFeatureEnabled(EFeatureToggles.ENHANCED_EQUIPMENT_TYPE_FILTER);

  equipmentTypeFilter: IEquipmentTypeFilter = {
    name: 'rel-product-sysivk',
    localizedName: this.translateService.instant('filters.equipment-filter.rel-product-sysivk'),
    isApplicable: false,
    activeValue: [],
    values: [],
  };

  equipmentComponentFilter: IEquipmentComponentFilter = {
    name: 'rel-sparepart-ivk',
    localizedName: this.translateService.instant('filters.equipment-component-filter.rel-sparepart-ivk'),
    isApplicable: false,
    activeValue: [],
    values: [],
  };

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  userRoles$!: Observable<EUserRoles[]>;
  private userRoleToToggleMap: Partial<Record<EUserRoles, EFeatureToggles>> = {
    [EUserRoles.Admin]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_CUSTOMER_ADMIN,
    [EUserRoles.Buyer]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_ORDER_USER,
    [EUserRoles.Approver]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_APPROVER_USER,
    [EUserRoles.Viewer]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BROWSER_USER,
    [EUserRoles.SPCViewer]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_SPAREPARTS_VIEW,
    [EUserRoles.BusinessPartnerBuyer]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BUSINESSPARTNER_BUYER,
    [EUserRoles.BusinessPartnerApprover]: EFeatureToggles.EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BUSINESSPARTNER_APPROVER,
  };

  ngOnInit(): void {
    this.equipmentComponentFilterActive = false;
    this.activeRoute = this.activatedRoute.snapshot.url[1].path;
    this.baseRouteUrl = '/' + this.activatedRoute.snapshot.url.map(url => url.path).join('/');
    this.userRoles$ = this.customerFacade.getCustomerCompanyRoles();
    this.initToggles();
    this.setSelectedFl();
    this.getComponentAndTypeFiltersVisibilities();
    this.selectIsCustomerBusinessPartner();
    this.setEquipmentTypeFilter();
    this.setEquipmentComponentFilter();
    this.selectUsFeatures();
    this.selectLoadingCartDataInProgress();
    this.selectCartAndSetParamsForFL();
    this.getBlockedSortByOptions();
    this.selectIsCategoriesLoaded();
    this.selectCategoriesSubscription();
    this.navigationBarEventSubscription();
    this.shouldShowWarning();
  }

  /**
   * function which is used to get Feature toggles and set them to the variables
   */
  initToggles(): void {
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.isMeaStore = AppUtils.isStoreActive(EStoreTypes.MEA);
    this.isMyInstalledBaseFlowEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW);
  }

  /**
   * Method to set feature toggles for CPQ and SpareParts
   */
  selectUsFeatures(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.CPQ).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isCpqEnabled = value;
    });

    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SPARE_PARTS).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isSparePartsEnabled = value;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngAfterViewChecked(): void {
    this.changeDetector.detectChanges();
  }

  /**
   * Method to set loading of categories
   */
  selectIsCategoriesLoaded(): void {
    this.catalogFacade.selectIsCategoriesLoaded().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loaded => {
      this.categoriesLoaded = loaded;
    });
  }

  /**
   *  Method to select category tree and call filterCatalogBasedOnTogglesAndStore() and initializePage()
   */
  selectCategoriesSubscription(): void {
    this.catalogFacade.selectCategories().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(categories => {
      this.categoryTree = categories ?? [];
      if (!this.categoriesLoaded) {
        return;
      }
      this.filterCatalogBasedOnTogglesAndStore();
      this.initializePage();
    });
  }

  /**
   * Method to initialize page
   */
  navigationBarEventSubscription(): void {
    this.router.events.pipe(
      take(1),
      filter(event => event instanceof NavigationEnd),
    ).subscribe(currentRouteEvent => {
      if (currentRouteEvent instanceof RouterEvent) {
        if (this.baseRouteUrl === currentRouteEvent.url) {
          this.initializePage();
        }
      }
    });
  }

  /**
   * Method to initialize page
   */
  initializePage(): void {
    this.activatedRouteSubscription = this.activatedRoute.url.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isRouteActive('catalog') && !this.activatedRoute.firstChild) {
          this.updateCategoryInUrl(true);
        } else if (this.activatedRoute.firstChild) {
          this.getQueryParamsFromChild();
        } else {
          this.getQueryParams();
        }
      });
  }

  /**
   * Method to get query params
   */
  getQueryParams(): void {
    this.activatedRoute.queryParams
      .pipe(debounceTime(0))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(routeParams => {
        this.resolveUrlParams(routeParams);

        if (this.isRouteActive('installed-base')) {
          if (!Object.prototype.hasOwnProperty.call(routeParams, 'rel-product-sysivk')) {
            this.router.navigate(['page-not-found']).then();
            return;
          }

          this.selectInstalledBase(routeParams['rel-product-sysivk']);
        }
      });
  }

  /**
   * Method to removes categories from catalogTree based on feature toggles when CPQ/SpareParts disabled will remove relevant category.
   * for now only for CA and US shop
   */
  filterCatalogBasedOnTogglesAndStore(): void {
    if (AppUtils.isSapStore() && (!this.isSparePartsEnabled || !this.isCpqEnabled)) {
      this.categoryTree = this.filterCategoriesByFeatures(this.categoryTree);
    }
  }

  /**
   * Method to remove Categories from category tree based on features toggles
   *
   * @param {ICategoryNode[]} categories
   * @returns {ICategoryNode[]}
   */
  filterCategoriesByFeatures(categories: ICategoryNode[]): ICategoryNode[] {
    const hiddenCategories: string[] = [];

    if (!this.isCpqEnabled) {
      hiddenCategories.push('service contracts');
    }

    if (!this.isSparePartsEnabled) {
      hiddenCategories.push('parts');
    }
    return categories?.filter(category => !hiddenCategories.includes(category.name?.toLocaleLowerCase()));
  }

  /**
   * Method to get query params from first child
   */
  getQueryParamsFromChild(): void {
    const firstChild: ActivatedRoute | null = this.activatedRoute.firstChild;
    if (!firstChild) {
      return;
    }

    combineLatest([
      firstChild.params,
      firstChild.queryParams,
    ]).pipe(takeUntil(this.unsubscribe$))
      .pipe(map(([params, queryParams]) => (
        {...params, ...queryParams}),
      )).pipe(debounceTime(0)).subscribe(routeParams => {
      this.activatedRouteSubscription.unsubscribe();
      this.resolveUrlParams(routeParams);
    });
  }

  /**
   * Method to select install base
   * @param {string} materialNumber
   */
  selectInstalledBase(materialNumber?: string): void {
    this.catalogFacade.selectInstalledBase().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(installedBase => {
      if (!!materialNumber && (!installedBase || this.installBaseProduct?.attributes.materialNumber !== materialNumber)) {
        this.setInstalledBase(materialNumber);
      } else {
        this.installBaseProduct = installedBase;
      }
    });
  }

  /**
   * Method to set install base
   * @param {string} materialNumber
   */
  setInstalledBase(materialNumber: string): void {
    this.installBaseFacade.selectInstalledBaseProductData()
      .pipe(
        takeUntil(this.unsubscribe$),
      ).subscribe(installBaseProducts => {
      if (!installBaseProducts) {
        return;
      }

      const installBaseProduct: ISimpleInstallBaseProduct | undefined = installBaseProducts.data.find(
        installBaseProduct => installBaseProduct.attributes.materialNumber === materialNumber,
      );

      if (!installBaseProduct) {
        return;
      }

      this.installBaseProduct = installBaseProduct;
      this.catalogFacade.setInstalledBase(this.installBaseProduct);
    });
  }

  /**
   * Method to resolve URL params
   * @param orgParams
   */
  resolveUrlParams(orgParams: Params): void {
    let params: Params = {};

    Object.keys(orgParams).forEach(key => {
      params[key] = orgParams[key];
    });

    if (!this.isRouteActive('installed-base') && params['fl-number']) {
      delete params['fl-number'];
    }

    if (params.category) {
      const categoryNode: ICategoryNode | null = CategoryUtils.getCategoryByParamFromTree(this.categoryTree, decodeURIComponent(params.category));

      if (!categoryNode) {
        this.updateCategoryInUrl();
        return;
      }

      params = {...params, ...{category: categoryNode.nodeId}};
    }

    if (params.page) {
      const pageLimit: number = this.params['page[limit]'] ?? 12;
      params = {
        ...params, ...{
          'page[limit]': pageLimit,
          'page[offset]': (params.page * pageLimit) - pageLimit,
        },
      };
    } else {
      params = {
        ...params, ...{
          'page[limit]': this.params['page[limit]'],
          'page[offset]': 0,
          page: 1,
        },
      };
    }

    if (this.isRouteActive('catalog')) {
      const catalogChildSegment: string = this.activatedRoute.firstChild?.snapshot.url[0].path ?? '';
      this.equipmentTypeFilter.isApplicable = false;

      if (catalogChildSegment === 'service-contracts') {
        if (this.contractParams['rel-product-sysivk']) {
          params = {...params, 'rel-product-sysivk': this.contractParams['rel-product-sysivk']};
        }
      }
    }

    this.params = params;
    this.catalogFacade.setQueryParams({...params});
    this.getSearchResults();
  }

  /**
   * Method to update category in url
   * @param {boolean} reInitialize
   */
  updateCategoryInUrl(reInitialize: boolean = false): void {
    this.catalogFacade.selectQueryParams().pipe(take(1)).subscribe(queryParams => {
      const categoryName: string = this.getCategoryToRedirectTo(this.categoryTree, reInitialize ? null : queryParams);
      const urlTree: UrlTree = this.router.parseUrl(this.router.url);
      const filteredQueryParams: Params = ObjectUtils.shallowFilterObject(urlTree.queryParams, 'category');

      if (!categoryName) {
        this.configurationFacade.setAlert({
            type: 'error',
            message: this.translate.instant('search-results.no-categories'),
            action: redirectToUrlExecuted.type,
            props: {url: '/' + this.activatedRoute.snapshot.url[0].path},
          },
        );
        return;
      }

      this.router.navigate(
        [`/catalog/${StringUtils.toLowerCaseWithoutWhiteSpaceOrSlash(categoryName, '-')}`],
        {queryParams: filteredQueryParams},
      ).then();
    });
  }

  /**
   * Method to get category to create redirect to category
   * @param {ICategoryNode[]} categoryTree
   * @param {ICurrentParams} params
   * @returns {string}
   */
  getCategoryToRedirectTo(categoryTree: ICategoryNode[], params: ICurrentParams | null): string {
    let categoryNode: ICategoryNode | null | undefined;

    if (params?.category) {
      categoryNode = CategoryUtils.getCategoryByIdFromTree(categoryTree, params.category);
    } else {
      categoryNode = categoryTree.find(parentCategory => parentCategory.hasProducts);
    }

    return categoryNode ? categoryNode.name : this.categoryTree[0]?.name ?? '';
  }

  /**
   * Method to get blocked sort by options
   */
  getBlockedSortByOptions(): void {
    this.catalogFacade.selectBlockedSortByOptions().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(blockedSortByOptions => this.blockedSortByOptions = blockedSortByOptions);
  }

  /**
   *  Method to get no products for BP and show filter
   *
   * @return void
   */
  getSearchResultsNoProductsBP(): void {
    this.loading = true;

    this.catalogFacade.getSearchResults(this.params).pipe(take(1)).subscribe(response => {
      this.loading = false;
      if (response) {
        const {pagination, valueFacets} = response.data[0].attributes;
        this.analyticsService.trackPageReady('Product list', PageTypes.PRODUCT_LIST_PAGE, undefined, this.params.q);
        this.pagination = this.mapPagination(pagination);
        this.fetchedCategories = valueFacets.find(facet => facet.name === 'category')?.values ?? [];

        this.filters = valueFacets.filter(valueFacet =>
          !this.notFilterableAttributes.includes(valueFacet.name) && (valueFacet.values.length > 0 || valueFacet.activeValue)
          && AppUtils.getCurrentStore().catalogFilters.includes(valueFacet.name),
        );
      }

      this.firstPageLoad = false;
    });
  }

  /**
   * Fetches search results based on the current URL parameters.
   * The method updates the loading state, fetches the data, and then updates the component's state with the received data.
   * It handles the response by setting the products, pagination, sorting options, and filters.
   * If no products are found and the user is a business partner, it triggers a secondary search.
   * If the catalog is for spare parts and contains products, it sets the catalog title accordingly.
   * also it is remaping parameter from 'other' to empty string for rel-sparepart-ivk parameter for
   * scenario when you want to search for products without equipment component relation
   * @returns {void}
   */
  getSearchResults(): void {
    this.loading = true;

    combineLatest([
      this.catalogFacade.getSearchResults(this.params),
      this.store.select(selectIsLoginInProcess).pipe(
        skipWhile(isLoginInProcess => isLoginInProcess === true),
        take(1),
      ),
    ]).pipe(take(1)).subscribe(([response]) => {
      this.loading = false;
      if (response) {
        this.setProducts(response);
        const {pagination, sort, valueFacets} = response.data[0].attributes;
        this.analyticsService.setProducts(response);
        this.analyticsService.trackPageReady('Product list', PageTypes.PRODUCT_LIST_PAGE, undefined, this.params.q);
        this.pagination = this.mapPagination(pagination);
        this.setSort(sort);
        this.fetchedCategories = valueFacets.find(facet => facet.name === 'category')?.values ?? [];

        const clonedCategoryTree: ICategoryNode[] = ObjectUtils.deepClone(this.categoryTree);
        const partsRootCategory: ICategoryNode | undefined = clonedCategoryTree.find(category => category.nodeId === 2);
        const partsCategory: IPartsCategory[] = CategoryUtils.filterCategoryChildrens(
          partsRootCategory ? [partsRootCategory] : [],
          this.fetchedCategories,
          this.params.category ?? 0,
        );

        this.equipmentTypeFilter.isApplicable = CategoryUtils.isParentCategoryActive(partsCategory) || this.noProductFound;
        if (this.equipmentTypeFilter.isApplicable) {
          this.resolveUrlParamsForPartsForEquipmentTypeFilter(this.params);
        }
        this.equipmentComponentFilter.isApplicable = this.isEquipmentSelected();
        if (this.equipmentComponentFilter.isApplicable) {
          if (this.equipmentComponentFilterActive) {
            this.setComponentFilterValueFacets(response);
          }
          this.resolveUrlParamsForPartsForEquipmentComponentFilter(this.params);
        }
        this.filters = valueFacets.filter(valueFacet =>
          !this.notFilterableAttributes.includes(valueFacet.name) && (valueFacet.values.length > 0 || valueFacet.activeValue)
          && AppUtils.getCurrentStore().catalogFilters.includes(valueFacet.name),
        );
        this.addActiveFilters();
      }

      this.firstPageLoad = false;

      if (this.noProductFound && this.isBusinessPartner) {
        const currentParams: Params = {...this.params};
        delete currentParams['fl-number'];
        delete currentParams['rel-product-sysivk'];
        this.params = currentParams;

        this.catalogFacade.setQueryParams({...this.params});
        this.getSearchResultsNoProductsBP();
      }

      if (!this.noProductFound && this.isBusinessPartner) {
        this.getSearchResultsNoProductsBP();
      }
    });
  }

  /**
   * Method to set equipment type filter
   */
  setEquipmentTypeFilter(): void {
    const uniqueEquipmentValues: Map<string, IEquipmentValues> = new Map<string, IEquipmentValues>();

    this.installBaseFacade.selectInstalledBaseProductData()
      .pipe(
        takeUntil(this.unsubscribe$),
      ).subscribe((installBaseSystems) => {
      if (!installBaseSystems) {
        return;
      }

      for (const installBaseSystem of installBaseSystems.data) {
        const materialName: string = installBaseSystem.attributes.materialName;
        const materialNumber: string = installBaseSystem.attributes.materialNumber;
        const key: string = `${materialName}-${materialNumber}`;

        if (!uniqueEquipmentValues.has(key)) {
          uniqueEquipmentValues.set(key, {
            value: materialName,
            'rel-product-sysivk': materialNumber,
          });
        }
      }

      this.equipmentTypeFilter.values = ArrayUtils.sortByAttribute([...uniqueEquipmentValues.values()], 'value');
    });
  }

  setEquipmentComponentFilter(): void {
    if (this.equipmentComponentFilterActive) {
      let equipmentComponentValues: IEquipmentComponentValues[] = [];
      this.catalogFacade.getSdmIbComponents(this.selectedFl)
        .pipe(
          skipWhile(sdmIbComponents => !sdmIbComponents),
          takeUntil(this.unsubscribe$),
        ).subscribe((sdmIbComponents: ISdmIbComponents) => {
        sdmIbComponents.data.forEach(sdmIbComponent => {
          if (sdmIbComponent.attributes.materialText === 'filters.equipment-component-filter.not-equipment-related') {
            equipmentComponentValues.push(
              {
                value: this.translateService.instant('filters.equipment-component-filter.not-equipment-related'),
                'rel-sparepart-ivk': sdmIbComponent.attributes.material,
                numberOfProducts: 0,
              },
            );
          } else {
            equipmentComponentValues.push(
              {
                value: sdmIbComponent.attributes.materialText,
                'rel-sparepart-ivk': sdmIbComponent.attributes.material,
                numberOfProducts: 0,
              },
            );
          }
        });
        equipmentComponentValues = ArrayUtils.removeDuplicates(equipmentComponentValues);
        this.equipmentComponentFilter.values = ArrayUtils.sortByAttribute(equipmentComponentValues, 'value');
      });
    }
  }

  /**
   * Method to set sorting
   * @param {ISort} sort
   */
  setSort(sort: ISort): void {
    this.sort = Object.entries<string>(sort.sortParamLocalizedNames).map(([key, name]) => {
      return {
        name: name,
        value: key,
      };
    });

    if (this.blockedSortByOptions.length > 0) {
      this.sort = this.sort.filter(option => !this.blockedSortByOptions.includes(option.value));
    }
  }

  /**
   * Method to add active filters
   */
  addActiveFilters(): void {
    const clonedFilters: IValueFacets[] = ObjectUtils.deepClone(this.filters);

    this.filters = clonedFilters.map(filterEntity => {
      if (Array.isArray(filterEntity.activeValue)) {
        const missingFilters: string[] = filterEntity.activeValue.filter(value =>
          !filterEntity.values.find(entity => entity.value === value));

        missingFilters.forEach(entity => filterEntity.values.push({value: entity, doc_count: 0}));
        return filterEntity;
      }
      if (filterEntity.activeValue) {
        const exists: unknown = filterEntity.values.find(entity => entity.value === filterEntity.activeValue);
        if (!exists) {
          filterEntity.values.push({value: filterEntity.activeValue, doc_count: 0});
        }
      }
      return filterEntity;
    });
  }

  /**
   * Method to set products
   * @param products
   */
  setProducts(products: ICatalogSearchIncluded): void {
    const {abstractProducts} = products.data[0].attributes;
    const includedProducts: IProductIncluded[] = products?.included ?? [];
    this.abstractProducts = abstractProducts.reduce<ICatalogProduct[]>((acc, product) => {
      const includes: IProductIncluded[] = includedProducts.filter(include => {
        const includeSku: string = include.type === 'concrete-products' ? include.attributes.productAbstractSku ?? '' : include.id;
        if (includeSku === product.abstractSku) {
          return include;
        }
      });
      const hasProductLabels: boolean = !!includes.find(include => include.type === 'abstract-products')?.relationships['product-labels'];
      if (hasProductLabels) {
        acc.push({...product, ...includes.reduce((ac, a) => ({...ac, ...a.attributes, ...a.relationships}), {})});
      } else {
        acc.push({...product, ...includes.reduce((ac, a) => ({...ac, ...a.attributes}), {})});
      }
      return acc;
    }, []);

    this.abstractProductIncluded = includedProducts;
    this.noProductFound = this.abstractProducts.length === 0;
    this.getLabels();
    this.setCpqSpecificCatalogTitle();
  }

  /**
   * Method to get labels
   * @returns {IAbstractProductIncluded[]}
   */
  getLabels(): IProductLabel[] {
    if (!this.abstractProductIncluded) {
      return [];
    }
    this.labels = this.abstractProductIncluded.filter((item): item is IProductLabel => item.type === 'product-labels');

    return this.labels;
  }

  private mapPagination(pagination: ICatalogPagination): IPagination {
    return {
      numFound: pagination.numFound,
      currentPage: pagination.currentPage,
      maxPage: pagination.maxPage,
      currentItemsPerPage: pagination.currentItemsPerPage,
      config: pagination.config ?? {
        parameterName: 'page',
        itemsPerPageParameterName: 'ipp',
        defaultItemsPerPage: pagination.currentItemsPerPage,
        validItemsPerPageOptions: [pagination.currentItemsPerPage],
      },
    };
  }

  /**
   * Method to show black friday label
   * @returns {IAbstractProductIncluded}
   */
  getBlackFridayLabel(): IProductLabel | null {
    if (this.labels?.length === 0) {
      return null;
    }

    return this.labels.find(item => item?.attributes?.frontEndReference?.toLocaleUpperCase() === 'BF') || null;
  }

  /**
   * Method to select layout option
   * @param option
   */
  selectLayoutOption(option: string): void {
    this.currentLayoutType = option;
  }

  /**
   * Method to check if route is active
   * @param {string} catalog
   * @returns {boolean}
   */
  isRouteActive(catalog: string): boolean {
    return this.activeRoute.includes(catalog);
  }

  /**
   * Method to show results message
   * @returns {boolean}
   */
  showResultsMessage(): boolean {
    return this.pagination && this.pagination.numFound >= 0 && !this.firstPageLoad && !!this.params.q;
  }

  /**
   * Method to proceed to homepage
   */
  proceedToHomepage(): void {
    this.router.navigate([this.activatedRoute.snapshot.url[0].path]).then();
  }

  /**
   * Method to proceed to catalog
   */
  proceedToCatalog(): void {
    this.router.navigate(['/catalog']).then();
  }

  /**
   * Method to show no configuration available info
   * @returns {boolean}
   */
  showNoConfigurationAvailableInfo(): boolean {
    if (!this.containsConfigurableProducts() || this.abstractProducts.length === 0) {
      return false;
    }
    return !!this.abstractProducts.find(product => !ProductUtils.isConfigurableAvailable(product));
  }

  /**
   * Method to check if it's spare parts catalog and if abstract products contains configurable products
   * @returns {boolean}
   */
  isSparePartsCatalogAndHasProducts(): boolean {
    return AppUtils.isSapStore() && this.abstractProducts?.length > 0
      && !this.abstractProducts.find(product => product?.productConfigurationInstance);
  }

  /**
   * Method to check if products have been found
   * @returns {boolean}
   */
  showNoProductFound(): boolean {
    return (this.isRouteActive('catalog') || this.isRouteActive('installed-base')) && this.noProductFound;
  }

  /**
   * Method to check if abstract products contains configurable products
   * @returns {boolean}
   */
  containsConfigurableProducts(): boolean {
    return !!this.abstractProducts.find(product => product?.productConfigurationInstance);
  }

  /**
   * Method to set CPQ specific catalog title
   */
  setCpqSpecificCatalogTitle(): void {
    if (this.isUsStore || this.isCaStore) {
      this.catalogTitle = this.containsConfigurableProducts() ? 'catalog.select-contract-type' : 'catalog.select-part-type';
    }
  }

  /**
   *  Method to set contract params
   * @param {Params} params
   */
  setContractParams(params: Params): void {
    const flNumber: string = params['fl-number'] ?? '';
    const relProductSysivk: string = params['rel-product-sysivk'] ?? '';

    this.contractParams['fl-number'] = flNumber;
    this.contractParams['rel-product-sysivk'] = relProductSysivk;
    this.hasContractParams = !!relProductSysivk.length && !!flNumber.length;
    this.catalogTitle = 'product-list.catalog';
  }

  /**
   * Method to resolve URL params for Parts and set equipment filter active value
   * @param {ICurrentParams} params
   * @returns {ICurrentParams}
   * @private
   */
  resolveUrlParamsForPartsForEquipmentTypeFilter(params: ICurrentParams): ICurrentParams {
    this.equipmentTypeFilter.activeValue = [];

    if (params['rel-product-sysivk']) {
      this.activeEquipmentType = Array.isArray(params['rel-product-sysivk'])
        ? params['rel-product-sysivk']
        : [params['rel-product-sysivk'].toString()];

      this.equipmentTypeFilter.activeValue = this.activeEquipmentType;
    }

    return params;
  }

  resolveUrlParamsForPartsForEquipmentComponentFilter(params: ICurrentParams): ICurrentParams {
    this.equipmentComponentFilter.activeValue = [];

    if (params['rel-sparepart-ivk']) {
      if (Array.isArray(params['rel-sparepart-ivk'])) {
        this.activeEquipmentComponent = params['rel-sparepart-ivk'];
      } else if (typeof params['rel-sparepart-ivk'] === 'string' && params['rel-sparepart-ivk'].includes(',')) {
        this.activeEquipmentComponent = params['rel-sparepart-ivk'].split(',');
      } else {
        this.activeEquipmentComponent = [params['rel-sparepart-ivk'].toString()];
      }

      this.equipmentComponentFilter.activeValue = this.activeEquipmentComponent;
    }
    return params;
  }

  /**
   * Method to select cart, cartId, systemDetails, queryParams and show warning message
   * @private
   */
  private selectCartAndSetParamsForFL(): void {
    combineLatest([
      this.marketingFacade.selectCart(),
      this.activatedRoute.queryParams,
    ]).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(([cart, params]) => {
      if (cart) {
        this.currentCart = cart;
        this.currentCartId = cart.id;
        if (cart.attributes?.systemDetails) {
          this.systemDetails = cart.attributes.systemDetails;
        }
        this.shouldShowWarning();
      }
      this.setContractParams(params);
    });
  }

  /**
   * Method to set loading of cart data
   * @private
   */
  private selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => {
      this.loadingCartDataInProgress = loadingCartDataInProgress;
    });
  }

  /**
   * Method for showing warning message
   * @private
   */
  private shouldShowWarning(): void {
    if (SparePartsUtils.canCartItemsBeChanged(this.currentCart, this.isBusinessPartner) && !this.warningWasShown) {
      this.showWarning();
      this.warningWasShown = true;
    }
  }

  /**
   * Method for creating warning notification
   * @private
   */
  private showWarning(): void {
    this.configurationFacade.appendNotification(warningMessageData);
  }

  /**
   * Method to set isBusinessPartner variable
   */
  selectIsCustomerBusinessPartner(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isBusinessPartner => {
      this.isBusinessPartner = isBusinessPartner;
    });
  }

  /**
   * method to determine if Equipment Component Filter
   * should be visible instead of Equipment type filter
   * if User have at least one role for which is
   * toggle is set to false Equipment type filter will be shown
   * instead of Equipment component filter
   * @private
   */
  private getComponentAndTypeFiltersVisibilities(): void {
    this.equipmentComponentFilterActive = true;
    this.userRoles$.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((userRoles: EUserRoles[]) => {
      userRoles.forEach((role: EUserRoles) => {
        const featureToggle: EFeatureToggles | undefined = this.userRoleToToggleMap[role];
        if (featureToggle) {
          this.configurationFacade.isFeatureEnabled(featureToggle).pipe(
            skipWhile(res => res === true),
            takeUntil(this.unsubscribe$),
          ).subscribe(() => {
            this.equipmentComponentFilterActive = false;
          });
        }
      });
    });
  }

  isEquipmentSelected(): boolean {
    let isEquipmentSelected: boolean = false;

    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (params['rel-product-sysivk']) {
        const equipmentValues: string[] = Array.isArray(params['rel-product-sysivk'])
          ? params['rel-product-sysivk']
          : [params['rel-product-sysivk'].toString()];

        if (equipmentValues.length > 0) {
          isEquipmentSelected = true;
        }
      }
    });

    return isEquipmentSelected;
  }

  setSelectedFl(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (params['fl-number']) {
        this.selectedFl = params['fl-number'];
      }
    });
  }

  private setComponentFilterValueFacets(response: ICatalogSearchIncluded): void {
    const componentFilterValueFacets: IValueFacets | undefined = response.data[0].attributes.valueFacets.find(facet => facet.name === 'rel_sparepart_ivk');
    this.equipmentComponentFilter.values.forEach(value => {
      //temporary solution until spryker provides explanantion why its not returning number of products for equipment component
      const numberOfProducts: number | undefined = componentFilterValueFacets?.values.find(facet => value['rel-sparepart-ivk'] === facet.value)?.doc_count;
      if (numberOfProducts !== undefined) {
        value.numberOfProducts = numberOfProducts;
      }
    });
  }
}
